import sanitizeHtml from 'sanitize-html'

export type SanitizedHtml = string & {__type: 'SanitizedHtml'}

const state: {
  wrapper: HTMLElement | undefined
} = {wrapper: undefined}

const emptyParagraphRx = '<p>(&nbsp;|\\s)*<\\/p>'

const nonBreakingSpace = ' ' // char code = 160
const instagramLinefeed = '⠀' // code = 10240
export const headingTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']
const tableTags = [
  'table',
  'thead',
  'th',
  'tbody',
  'tr',
  'td',
  'colgroup',
  'col'
]

const attributesWithClassAndStyle = [
  ...tableTags,
  ...headingTags,
  'p',
  'span',
  'figure',
  'label',
  'ul',
  'ol',
  'li'
].reduce(
  (acc, item) => {
    acc[item] = ['class', 'style']
    return acc
  },
  {} as {[key: string]: string[]}
)

export class HTMLUtil {
  public static sanitizeHtml(
    html: string,
    opts: {
      allowedTags?: string[]
      allowedAttributes?: {[tag: string]: string[]}
      disallowedTags?: string[]
      escapeInvalid?: boolean
    } = {}
  ): SanitizedHtml {
    const defaultTags = [
      ...headingTags,
      'u',
      'b',
      'i',
      'em',
      'strike',
      's',
      'strong',
      'div',
      'span',
      'p',
      'br',
      'a',
      'figure',
      'img',
      'ul',
      'ol',
      'li',
      'label',
      'wrapper',
      'video',
      'source',
      'hr',
      'oembed',
      'input',
      ...tableTags
    ].filter(t => !opts.disallowedTags || !opts.disallowedTags.includes(t))
    const defaultAttributes = {
      img: ['src', 'alt', 'class', 'style'],
      a: ['href', 'name', 'target', 'rel', 'class', 'style'],
      ...attributesWithClassAndStyle,
      td: ['class', 'style', 'colspan', 'rowspan'],
      oembed: ['url', 'class', 'style'],
      input: ['type', 'disabled', 'checked'],
      video: [
        'controls',
        'class',
        'autoplay',
        'style',
        'src',
        'preload',
        'loop',
        'muted'
      ],
      source: ['src'],
      div: ['class', 'style']
    }

    return sanitizeHtml(html, {
      allowedTags: [...defaultTags, ...(opts.allowedTags ?? [])],
      disallowedTagsMode: opts.escapeInvalid ? 'recursiveEscape' : 'discard',
      allowedSchemes: ['data', 'http', 'https'],
      allowedAttributes: {
        ...defaultAttributes,
        ...(opts.allowedAttributes || {})
      }
    }) as SanitizedHtml
  }

  public static extractText(
    html: string,
    includeOther = false,
    forceSanitize = false
  ): string {
    if (typeof document === 'undefined' || forceSanitize) {
      const sanitized = sanitizeHtml(html, {allowedTags: ['br']})
      const replaced = sanitized.replace(/<br \/>/gi, '\n')
      return replaced
    }
    if (!state.wrapper) {
      const w = document.implementation.createHTMLDocument(
        'UtilHtml.extractText'
      )
      state.wrapper = w.createElement('div')
      state.wrapper.style.opacity = '0'
      state.wrapper.style.pointerEvents = 'none'
      state.wrapper.style.whiteSpace = 'pre-wrap'
      // document.body.appendChild(state.wrapper)
    }
    state.wrapper.innerHTML = sanitizeHtml(html)

    const extract = (
      node: Node,
      topLevel: boolean
    ): {text: string; isBlock: boolean} => {
      // Text node
      if (node.nodeType === 3) {
        return {text: node.nodeValue ?? '', isBlock: false}
      }

      // Element node
      //
      if (node instanceof Element) {
        if (includeOther && node.tagName === 'IMG') {
          return {text: (node as HTMLImageElement).alt, isBlock: false}
        } else if (includeOther && node.tagName === 'INPUT') {
          return {text: (node as HTMLInputElement).value, isBlock: false}
        } else {
          let res = ''
          let lastChildIsBlock = false
          for (let i = 0; i < node.childNodes.length; i++) {
            const child = node.childNodes[i]
            const {text, isBlock} = extract(child, false)
            if (i > 0 && !lastChildIsBlock && isBlock) {
              res += '\n'
            }
            res += text
            lastChildIsBlock = isBlock
          }
          const display = getComputedStyle(node).getPropertyValue('display')
          const elIsBlock =
            display.startsWith('block') || display.startsWith('list')
          const isEmpty = !res

          if (node.tagName === 'BR') {
            res += '\n'
          } else if (!lastChildIsBlock && elIsBlock && !isEmpty) {
            res += '\n'
          }
          if (topLevel && elIsBlock && res.endsWith('\n')) {
            res = res.slice(0, -1)
          }
          return {text: res, isBlock: elIsBlock}
        }
      } else {
        // not an element
        return {text: '', isBlock: false}
      }
    }

    return extract(state.wrapper, true).text
  }

  public static escapeHtml(rawString: string): string {
    if (!rawString) {
      return rawString
    }
    const el = document.createElement('div')
    el.innerText = rawString
      .replace(new RegExp(nonBreakingSpace, 'g'), '---S---')
      .replace(/\n/g, '---N---')
      .replace(/<br[^>]*>/g, '---N---')
      .replace(/\r/g, '---R---')
      .replace(/\t/g, '---T---')
    return el.innerHTML
      .replace(/---S---/g, nonBreakingSpace)
      .replace(/---N---/g, '\n')
      .replace(/---R---/g, '\r')
      .replace(/---T---/g, '\t')
  }

  /**
   * UNSAFE! Unescapes HTML in the provided string. Consider using sanitization before unescaping
   * See https://stackoverflow.com/a/3700369
   */
  public static unescapeHtmlUnsafe(rawString: string): string {
    const el = document.createElement('textarea')
    el.innerHTML = rawString
    return el.value
  }

  /**
   * Removes paragraphs, lists&list items from an html string substituting them
   * with line breaks
   */
  public static messageTextHtmlToMessageText(s: string): string {
    // replace empty paragraphs with line breaks
    s = s.replace(new RegExp(emptyParagraphRx, 'g'), '\n')
    // remove opening block tags
    s = s.replace(/<(p|ul|ol)>/g, '')
    // prefix list items with dash
    s = s.replace(/<li>/g, '- ')
    // convert closing tags to line breaks
    s = s.replace(/<\/(p|ul|ol|li)>/g, '\n')
    // all other tags must be replaces with their text content
    s = sanitizeHtml(s, {allowedTags: [], allowedAttributes: {}})
    // unescape safe HTML entities
    s = HTMLUtil.unescapeHtmlUnsafe(s)
    return s.trim()
  }

  /**
   * Wraps text paragraphs separated by line breaks in paragraph tags
   */
  public static lineBreakToParagraph(s: string): string {
    s = s.replace(/\n/g, '</p><p>')
    s = s.replace(
      new RegExp(instagramLinefeed, 'g'),
      instagramLinefeed + '</p><p>'
    )
    s = `<p>${s}</p>`
    return s
  }

  /**
   * Removes leading and trailing empty paragraphs and trims internal ones
   */
  public static trimEmptyParagraphs(s: string): string {
    // trim empty space at the end of paragraphs
    s = s.replace(/(&nbsp;|\s)+<\/p>/g, '</p>')
    // add &nbsp; to paragraphs with no content so that they render properly
    s = s.replace(/<p><\/p>/g, '<p>&nbsp;</p>')
    // trim leading empty paragraphs
    s = s.replace(new RegExp(`^(${emptyParagraphRx})+`), '')
    // trim trailing empty paragraphs
    s = s.replace(new RegExp(`(${emptyParagraphRx})+$`), '')
    // substitute internal empty paragraphs with simpler markup
    s = s.replace(new RegExp(emptyParagraphRx, 'g'), '<p>&nbsp;</p>')
    return s.trim()
  }

  /**
   * Converts line breaks to `<br/>`
   */
  public static lineBreakToBr(s: string): string {
    s = s.replace(/\n/g, '<br/>')
    s = s.replace(
      new RegExp(instagramLinefeed, 'g'),
      instagramLinefeed + '<br/>'
    )
    return s
  }

  /**
   * Check if provided HTML has more tag types than just paragraphs (e.g. also
   * bold, italic tags)
   */
  public static hasComplexFormatting(s: string): boolean {
    return sanitizeHtml(s, {allowedTags: ['p'], allowedAttributes: {}}) !== s
  }

  public static trimLineBreaks(s: string): string {
    return s.trim().replace(/\r?\n/g, ' ')
  }
}
